import type {
  NotificationReachResponse,
  QuietNotificationOrgsResponse,
} from '@nadoutong/contracts';
import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import {
  type QuietNotificationOrgsQuery,
  getNotificationReach,
  listQuietNotificationOrgs,
} from './notificationReach.ts';

/**
 * React Query wrappers for #1947's two reachability reads.
 *
 * Read-only, so there is no invalidation orchestration: the numbers are
 * recomputed server-side on every read and grow on their own as late clients
 * poll, which is the behaviour #1947 asks for. Sending an announcement does
 * not need to invalidate reach either — a broadcast that was just written has
 * a reach of zero until somebody polls, and re-fetching to learn that costs a
 * round trip to confirm the obvious.
 */

export const NOTIFICATION_REACH_ROOT_KEY = ['notification-reach'] as const;
export const QUIET_NOTIFICATION_ORGS_ROOT_KEY = ['quiet-notification-orgs'] as const;

export function useNotificationReach(
  fingerprints: readonly string[],
): UseQueryResult<NotificationReachResponse> {
  const { fetchImpl } = useSession();
  // Sorted into the key so two renders of the same page in a different row
  // order share one cache entry instead of thrashing it.
  const key = [...fingerprints].sort();
  return useQuery({
    queryKey: [...NOTIFICATION_REACH_ROOT_KEY, key] as const,
    queryFn: ({ signal }) => getNotificationReach(fingerprints, fetchImpl, signal),
    // Nothing to ask about before the history page has loaded.
    enabled: fingerprints.length > 0,
  });
}

export function useQuietNotificationOrgs(
  query: QuietNotificationOrgsQuery,
): UseQueryResult<QuietNotificationOrgsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...QUIET_NOTIFICATION_ORGS_ROOT_KEY, query] as const,
    queryFn: ({ signal }) => listQuietNotificationOrgs(query, fetchImpl, signal),
  });
}
